import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

import { QRCodeSVG } from 'qrcode.react';

const QRGenerationPanel = ({ onQRGenerated, isStaffMember }) => {
  const { user, currentTenant, userProfile } = useAuth();
  const [qrType, setQrType] = useState('profile');
  const [selectedMember, setSelectedMember] = useState('');
  const [expiryHours, setExpiryHours] = useState('24');
  const [members, setMembers] = useState([]);
  const [generatedQR, setGeneratedQR] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isStaffMember && qrType === 'member_account') {
      fetchMembers();
    }
  }, [isStaffMember, qrType]);

  const fetchMembers = async () => {
    setLoadingMembers(true);
    try {
      const { data, error } = await supabase
        ?.from('tenant_members')
        ?.select(`
          *,
          user_profiles!tenant_members_user_id_fkey(id, full_name, email)
        `)
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('is_active', true)
        ?.order('user_profiles(full_name)');

      if (error) throw error;
      setMembers(data || []);
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleGenerateQR = async () => {
    setLoading(true);
    setSuccess('');
    setError('');
    setGeneratedQR(null);

    try {
      let qrData;
      let targetUserId;
      let expiresAt = null;

      if (qrType === 'profile') {
        // Member generating their own profile QR
        targetUserId = user?.id;
        const profileUrl = `${window.location?.origin}/member-profile?user_id=${user?.id}`;
        qrData = `PROFILE-${currentTenant?.id?.substring(0, 8)}-${user?.id?.substring(0, 8)}-${Date.now()}`;
        
        // One-time use - expires in specified hours
        const expiryDate = new Date();
        expiryDate?.setHours(expiryDate?.getHours() + parseInt(expiryHours));
        expiresAt = expiryDate?.toISOString();
      } else if (qrType === 'member_account' && isStaffMember) {
        // Staff generating QR for member account
        if (!selectedMember) {
          setError('Please select a member');
          setLoading(false);
          return;
        }
        targetUserId = selectedMember;
        qrData = `MEMBER-${currentTenant?.id?.substring(0, 8)}-${selectedMember?.substring(0, 8)}-${Date.now()}`;
        
        // Set expiry if specified
        if (expiryHours && expiryHours !== 'never') {
          const expiryDate = new Date();
          expiryDate?.setHours(expiryDate?.getHours() + parseInt(expiryHours));
          expiresAt = expiryDate?.toISOString();
        }
      }

      // Insert QR code into database
      const { data, error } = await supabase
        ?.from('member_barcodes')
        ?.insert({
          tenant_id: currentTenant?.id,
          user_id: targetUserId,
          barcode_data: qrData,
          barcode_type: 'qr_code',
          is_active: true,
          expires_at: expiresAt,
          generated_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (error) throw error;

      setGeneratedQR(data);
      setSuccess('QR code generated successfully!');
      onQRGenerated?.();
    } catch (error) {
      console.error('Error generating QR code:', error);
      setError('Failed to generate QR code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById('generated-qr-code');
    const svgData = new XMLSerializer()?.serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas?.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img?.width;
      canvas.height = img?.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas?.toDataURL('image/png');
      
      const downloadLink = document.createElement('a');
      downloadLink.download = `qr-code-${generatedQR?.barcode_data}.png`;
      downloadLink.href = pngFile;
      downloadLink?.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const expiryOptions = [
    { value: '1', label: '1 Hour', description: 'Expires in 1 hour' },
    { value: '6', label: '6 Hours', description: 'Expires in 6 hours' },
    { value: '24', label: '24 Hours', description: 'Expires in 1 day' },
    { value: '72', label: '3 Days', description: 'Expires in 3 days' },
    { value: '168', label: '1 Week', description: 'Expires in 7 days' },
    { value: 'never', label: 'Never', description: 'No expiration' }
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Generation Form */}
      <div className="bg-card rounded-lg p-6 border border-border">
        <h2 className="text-xl font-bold mb-6">Generate New QR Code</h2>

        <div className="space-y-6">
          {/* QR Type Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">
              QR Code Type
            </label>
            <div className="space-y-2">
              <label className="flex items-center p-3 border border-border rounded-lg cursor-pointer hover:bg-muted transition-colors">
                <input
                  type="radio"
                  name="qrType"
                  value="profile"
                  checked={qrType === 'profile'}
                  onChange={(e) => setQrType(e?.target?.value)}
                  className="mr-3"
                />
                <div>
                  <p className="font-medium">My Profile QR Code</p>
                  <p className="text-xs text-muted-foreground">One-time QR code linking to your profile</p>
                </div>
              </label>
              {isStaffMember && (
                <label className="flex items-center p-3 border border-border rounded-lg cursor-pointer hover:bg-muted transition-colors">
                  <input
                    type="radio"
                    name="qrType"
                    value="member_account"
                    checked={qrType === 'member_account'}
                    onChange={(e) => setQrType(e?.target?.value)}
                    className="mr-3"
                  />
                  <div>
                    <p className="font-medium">Member Account QR Code</p>
                    <p className="text-xs text-muted-foreground">Generate QR code for any member account</p>
                  </div>
                </label>
              )}
            </div>
          </div>

          {/* Member Selection - Staff Only */}
          {qrType === 'member_account' && isStaffMember && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Select Member
              </label>
              <Select
                value={selectedMember}
                onChange={(value) => setSelectedMember(value)}
                options={members?.map(member => ({
                  value: member?.user_profiles?.id,
                  label: member?.user_profiles?.full_name,
                  description: member?.user_profiles?.email
                }))}
                placeholder="Choose a member"
                searchable
                loading={loadingMembers}
                disabled={loading}
              />
            </div>
          )}

          {/* Expiry Time */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Expiration Time
            </label>
            <Select
              value={expiryHours}
              onChange={(value) => setExpiryHours(value)}
              options={expiryOptions}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground mt-1">
              QR code will automatically expire after this duration
            </p>
          </div>

          {/* Success Message */}
          {success && (
            <div className="p-3 bg-success/10 border border-success rounded-lg">
              <p className="text-sm text-success flex items-center gap-2">
                <Icon name="CheckCircle" size={16} />
                {success}
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive rounded-lg">
              <p className="text-sm text-destructive flex items-center gap-2">
                <Icon name="AlertCircle" size={16} />
                {error}
              </p>
            </div>
          )}

          {/* Generate Button */}
          <Button
            onClick={handleGenerateQR}
            disabled={loading || (qrType === 'member_account' && !selectedMember)}
            loading={loading}
            iconName="QrCode"
            className="w-full"
          >
            Generate QR Code
          </Button>
        </div>
      </div>
      {/* QR Code Display */}
      <div className="bg-card rounded-lg p-6 border border-border">
        <h2 className="text-xl font-bold mb-6">Generated QR Code</h2>

        {generatedQR ? (
          <div className="space-y-6">
            {/* QR Code */}
            <div className="flex justify-center p-8 bg-white rounded-lg">
              <QRCodeSVG
                id="generated-qr-code"
                value={generatedQR?.barcode_data}
                size={256}
                level="H"
                includeMargin
              />
            </div>

            {/* QR Info */}
            <div className="space-y-3">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">QR Code ID</p>
                <p className="font-mono text-sm break-all">{generatedQR?.barcode_data}</p>
              </div>

              {generatedQR?.expires_at && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Expires At</p>
                  <p className="text-sm font-medium">
                    {new Date(generatedQR?.expires_at)?.toLocaleString()}
                  </p>
                </div>
              )}

              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Status</p>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                  <Icon name="CheckCircle" size={12} />
                  Active
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={handleDownloadQR}
                variant="outline"
                iconName="Download"
              >
                Download
              </Button>
              <Button
                onClick={() => window.print()}
                variant="outline"
                iconName="Printer"
              >
                Print
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <Icon name="QrCode" size={64} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">
              Generate a QR code to see it here
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRGenerationPanel;